import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useFetcher, useNavigate } from 'react-router'
import { ElseBel } from '#app/components/else/else-dag.tsx'
import { OyField } from '#app/components/oy/oy-field.tsx'
import { OyTabBar, type OyTabItem } from '#app/components/oy/oy-nav.tsx'
import {
	OyPhotoPicker,
	OyPhotoViewer,
	photoSrc,
	type PickedPhoto,
} from '#app/components/oy/oy-photo.tsx'
import { useOyToast } from '#app/components/oy/oy-toast.tsx'
import { OyTray, OyTrayField } from '#app/components/oy/oy-tray.tsx'
import { cn } from '#app/utils/misc.tsx'
import { type ElseShell } from '#app/utils/stal-else.server.ts'

/**
 * Herontwerp P7 — Else's chrome in the Stal-app (StalAppRollen role=else):
 * the Else scale (`.oy-else`, plan §1.5: nothing under 18px), the five
 * 68px tabs (Vandaag, Week, Groep, Foto's, Gedaan; line icons, fase 5b),
 * the warm door while her `e_stal_surface` sentence is not signed, and
 * her two trays: "Foto's erbij" (a 2-column picker, sequential upload,
 * one message in the group) and the photo viewer. Every string here is
 * chrome copy at the Else register — short, positive, never a bot.
 */

export const ELSE_STAL_TABS: OyTabItem[] = [
	{
		to: '/stal/else',
		label: 'Vandaag',
		icon: 'horse',
		iconFill: 'horse-fill',
		end: true,
	},
	{
		to: '/stal/else/week',
		label: 'Week',
		icon: 'calendar-blank',
		iconFill: 'calendar-blank-fill',
	},
	{
		to: '/stal/else/groep',
		label: 'Groep',
		icon: 'chat-circle',
		iconFill: 'chat-circle-fill',
	},
	{
		to: '/stal/else/fotos',
		label: "Foto's",
		icon: 'image',
		iconFill: 'image-fill',
	},
	{
		to: '/stal/else/gedaan',
		label: 'Gedaan',
		icon: 'check-circle',
		iconFill: 'check-circle-fill',
	},
]

export const ELSE_DOORGEVEN = '/stal/else/doorgeven'
export const ELSE_FOTO_UPLOAD = '/stal/else/foto-upload'

export function ElseStalChrome({
	shell,
	children,
}: {
	shell: ElseShell
	children: React.ReactNode
}) {
	return (
		<div className="st-shell st-else oy-else">
			{shell.consent.surface ? (
				children
			) : (
				<main className="st-main st-else-main">
					<div className="st-else-door">
						<span className="oy-stal-brand" aria-hidden="true">
							🐴
						</span>
						<h1>Hoi {shell.firstName}</h1>
						<p>
							Je paardenpagina staat bijna klaar. Je ochtendbericht komt gewoon.
						</p>
						{shell.call ? (
							<ElseBel label={shell.call.label} href={shell.call.href} />
						) : null}
					</div>
				</main>
			)}
			<OyTabBar items={ELSE_STAL_TABS} label="Menu" big />
		</div>
	)
}

/** The page head at the Else scale: date line 22/700 muted, title 40/800. */
export function ElseHead({
	kicker,
	title,
	end,
}: {
	kicker: string
	title: string
	end?: React.ReactNode
}) {
	return (
		<div className="st-else-head">
			<div>
				<p className="oy-kicker">{kicker}</p>
				<h1>{title}</h1>
			</div>
			{end}
		</div>
	)
}

export function ElseCard({
	label,
	tone,
	className,
	children,
}: {
	label?: string
	tone?: 'hero' | 'paard' | 'regel' | 'done'
	className?: string
	children: React.ReactNode
}) {
	return (
		<section className={cn('st-else-card', tone && `tone-${tone}`, className)}>
			{label ? <div className="oy-else-label">{label}</div> : null}
			{children}
		</section>
	)
}

// ── trays ───────────────────────────────────────────────────────────────────

export type ElseTrayState =
	| { kind: 'foto' }
	| { kind: 'foto-view'; photoId: string; label: string | null; day?: string }

type TrayApi = { open: (t: ElseTrayState) => void; close: () => void }
const TrayContext = createContext<TrayApi | null>(null)

export function useElseTray(): TrayApi {
	const api = useContext(TrayContext)
	if (!api) throw new Error('useElseTray needs an ElseTrayProvider')
	return api
}

function useRetained<T>(value: T | null): T | null {
	const ref = useRef<T | null>(value)
	if (value !== null) ref.current = value
	return value ?? ref.current
}

export function ElseTrayProvider({
	shell,
	children,
}: {
	shell: ElseShell
	children: React.ReactNode
}) {
	const [state, setState] = useState<ElseTrayState | null>(null)
	const api = useMemo<TrayApi>(
		() => ({ open: (t) => setState(t), close: () => setState(null) }),
		[],
	)
	const shown = useRetained(state)
	return (
		<TrayContext.Provider value={api}>
			{children}
			{shown?.kind === 'foto' ? (
				<ElseFotoTray
					key="foto"
					shell={shell}
					open={state !== null}
					onClose={api.close}
				/>
			) : shown?.kind === 'foto-view' ? (
				<ElseFotoViewTray
					key={`view:${shown.photoId}`}
					open={state !== null}
					onClose={api.close}
					photoId={shown.photoId}
					label={shown.label}
					day={shown.day}
				/>
			) : null}
		</TrayContext.Provider>
	)
}

type UploadResult = { ok: true; id: string } | { ok: false; error: string }
type DoorgevenResult =
	{ ok: true; toast: string } | { ok: false; error: string }

function ElseFotoTray({
	shell,
	open,
	onClose,
}: {
	shell: ElseShell
	open: boolean
	onClose: () => void
}) {
	const toast = useOyToast()
	const navigate = useNavigate()
	const [picked, setPicked] = useState<PickedPhoto[]>([])
	const [caption, setCaption] = useState('')
	const [busy, setBusy] = useState(0)
	const [error, setError] = useState<string | null>(null)
	const pickedRef = useRef(picked)
	pickedRef.current = picked
	useEffect(
		() => () => pickedRef.current.forEach((p) => URL.revokeObjectURL(p.src)),
		[],
	)
	const n = picked.length
	const disabled = !shell.consent.fotos || !shell.consent.chat
	async function send() {
		if (!n || busy) return
		setError(null)
		const ids: string[] = []
		for (let i = 0; i < picked.length; i++) {
			setBusy(i + 1)
			const fd = new FormData()
			fd.set('file', picked[i]!.file)
			const res = await fetch(ELSE_FOTO_UPLOAD, { method: 'post', body: fd })
			const json = (await res.json().catch(() => null)) as UploadResult | null
			if (!res.ok || !json?.ok) {
				setBusy(0)
				setError(
					json && !json.ok
						? json.error
						: 'Dat lukte even niet. Probeer het nog eens.',
				)
				return
			}
			ids.push(json.id)
		}
		const body = new URLSearchParams({
			intent: 'groep-foto',
			photoIds: ids.join(','),
			caption,
		})
		const res = await fetch(ELSE_DOORGEVEN, { method: 'post', body })
		const json = (await res.json().catch(() => null)) as DoorgevenResult | null
		setBusy(0)
		if (!json?.ok) {
			setError(json && !json.ok ? json.error : 'Dat lukte even niet.')
			return
		}
		toast.say(json.toast)
		setPicked([])
		setCaption('')
		onClose()
		void navigate('/stal/else/groep')
	}
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="📷"
			title="Foto's erbij"
			intro={
				disabled
					? "Foto's komen er zo aan."
					: n
						? `${n} gekozen.`
						: "Kies foto's uit je album. Ze komen in de groep."
			}
			footer={{
				cancel: null,
				primary: {
					label: busy
						? `Bezig, ${busy} van ${n}…`
						: n
							? `Zet ${n} in de groep`
							: "Tik je foto's aan",
					disabled: !n || busy > 0 || disabled,
					onClick: () => void send(),
				},
			}}
			className="st-else-tray"
		>
			<OyTrayField label={n ? 'Gekozen' : "Foto's"}>
				<div className="st-else-pick">
					<OyPhotoPicker
						picked={picked}
						disabled={disabled || busy > 0}
						onPick={(files) =>
							setPicked((cur) =>
								[
									...cur,
									...files.map((file) => ({
										key: `${file.name}:${file.size}:${file.lastModified}`,
										file,
										src: URL.createObjectURL(file),
									})),
								].filter(
									(p, i, arr) => arr.findIndex((x) => x.key === p.key) === i,
								),
							)
						}
						onRemove={(key) =>
							setPicked((cur) => {
								const gone = cur.find((p) => p.key === key)
								if (gone) URL.revokeObjectURL(gone.src)
								return cur.filter((p) => p.key !== key)
							})
						}
					/>
				</div>
			</OyTrayField>
			<OyTrayField label="Zeg er iets bij (mag leeg)">
				<OyField
					value={caption}
					onChange={(e) => setCaption(e.target.value)}
					placeholder="bijv. Was gezellig"
					maxLength={280}
					disabled={disabled}
				/>
			</OyTrayField>
			{error ? (
				<p className="st-status warm" role="status">
					{error}
				</p>
			) : null}
		</OyTray>
	)
}

function ElseFotoViewTray({
	open,
	onClose,
	photoId,
	label,
	day,
}: {
	open: boolean
	onClose: () => void
	photoId: string
	label: string | null
	day?: string
}) {
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🖼"
			title={label ?? 'Foto'}
			intro={day}
			footer={{
				cancel: null,
				primary: { label: 'Sluiten', tone: 'quiet', onClick: onClose },
			}}
			className="st-else-tray"
		>
			<OyPhotoViewer src={photoSrc(photoId)} alt={label ?? 'foto'} meta={[]} />
		</OyTray>
	)
}

/** A fetcher-backed toggle for the Gedaan cards and the quick replies. */
export function useElseWrite() {
	const fetcher = useFetcher<DoorgevenResult>()
	const toast = useOyToast()
	const seen = useRef<DoorgevenResult | null>(null)
	useEffect(() => {
		if (
			fetcher.state !== 'idle' ||
			!fetcher.data ||
			fetcher.data === seen.current
		)
			return
		seen.current = fetcher.data
		toast.say(fetcher.data.ok ? fetcher.data.toast : fetcher.data.error)
	}, [fetcher.state, fetcher.data, toast])
	return {
		post: (fields: Record<string, string>) =>
			void fetcher.submit(fields, { method: 'post', action: ELSE_DOORGEVEN }),
		busy: fetcher.state !== 'idle',
		data: fetcher.data,
	}
}
